import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Card } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProductLink } from "../../components/ProductLink";
import { ProductClient, type ProductDTO, type ProductStatus } from "../../api/product";

const STATUS_VARIANT: Record<ProductStatus, "success" | "warning" | "error" | "info"> = {
  AVAILABLE: "success",
  PENDING_LAUNCH: "info",
  PENDING_DISCONTINUED: "warning",
  DISCONTINUED: "error",
};

/**
 * The bundles this item is sold inside.
 *
 * The inverse of a bundle's Items tab, and the question that has no answer
 * anywhere else in the app: before discontinuing an item, or changing its
 * carton, you need to know what else breaks. Following the composition the
 * other way meant opening bundles one at a time and looking.
 *
 * Read-only on purpose. Membership belongs to the bundle, and it is already
 * editable there; offering it in both directions would give two places to make
 * the same mistake.
 */
export function BundlesTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["product", "bundles-containing", draft.id],
    queryFn: () => client.bundlesContaining(draft.id),
    enabled: Boolean(draft.id),
  });

  const bundles = data ?? [];

  if (isLoading) {
    return <p className="text-[13px] text-ink-faint">{t("common.loading", "Loading...")}</p>;
  }

  if (bundles.length === 0) {
    return (
      <Card className="p-5">
        <p className="text-[13px] text-ink-faint">
          {t("product.bundles.none", "This item is not part of any bundle.")}
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <h3 className="mb-4 text-[13px] font-semibold text-ink">
        {t("product.bundles.title", "Sold inside these bundles")}
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px] uppercase tracking-wider text-ink-faint">
              <th className="pb-2 pr-3 font-medium">{t("column.sku", "SKU")}</th>
              <th className="pb-2 pr-3 font-medium">{t("column.name", "Name")}</th>
              <th className="pb-2 pr-3 font-medium">{t("column.status", "Status")}</th>
              <th className="pb-2 text-right font-medium">
                {t("product.bundles.quantity", "Qty in bundle")}
              </th>
            </tr>
          </thead>
          <tbody>
            {bundles.map((bundle) => {
              // The quantity lives on the bundle's own line for this item, which
              // is why it is read from the bundle rather than passed in.
              const line = bundle.group_lines?.find((entry) => entry.product_id === draft.id);
              return (
                <tr key={bundle.id} className="border-t border-line-soft">
                  <td className="py-1.5 pr-3 font-medium">
                    <ProductLink id={bundle.id}>{bundle.metadata?.sku ?? ""}</ProductLink>
                  </td>
                  <td className="py-1.5 pr-3 text-ink-muted">
                    {bundle.description_en?.product_name ?? ""}
                  </td>
                  <td className="py-1.5 pr-3">
                    {bundle.status && (
                      // Rendered raw, the way the products grid does it: one
                      // tab inventing translated status labels would just make
                      // the same value read two different ways in one app.
                      <Badge variant={STATUS_VARIANT[bundle.status] ?? "neutral"} size="sm">
                        {bundle.status}
                      </Badge>
                    )}
                  </td>
                  <td className="tabular py-1.5 text-right">{line?.quantity ?? ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
